import React, { useMemo } from 'react';
import { useQuery } from 'react-query';

import styled from 'styled-components';

import { fetchSwotCategoriesScores } from 'apis/SwotAPI';
import { usePublicationContext } from 'components/Publication/PublicationContext';
import { useCommunityViewContext } from 'components/View/CommunityViewContext';
import { useWidgetDataFetcher } from 'hooks/useWidgetDataFetcher';
import { toNullableNumber } from 'utils/helpers/numbers';

import SwotPerformanceScatterGraph from './SwotPerformanceScatterGraph';
import { getAverageScore } from './utils';
import { SWOT_CATEGORIES } from '../constants';
import { SwotCategoryScoresInbound } from '../types';
import NoDataInfo from '../Widget/NoDataContent';
import { useWidgetContext } from '../Widget/WidgetContext';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: start;
  flex-grow: 1;
  height: 100%;
  overflow: hidden;
`;

export const SwotPerformanceWidgetContent = () => {
  const { communityId } = useCommunityViewContext();
  const { type, filters, rangeType } = useWidgetContext();
  const { fetchWidgetData, showContractLabelAnnotations, snapshotUuids } = useWidgetDataFetcher();
  const { publication } = usePublicationContext();
  const {
    competitors,
    careTypes,
    roomTypes,
    roomTypesPriceDisplay,
    careLevels,
    reviewSources,
    reviewCategories,
    swotCategories,
    contractTypes,
  } = filters;
  const { data: swotAverages } = useQuery({
    queryKey: ['swot-performance', type, communityId, filters, showContractLabelAnnotations],
    queryFn: ({ signal }) =>
      fetchWidgetData(
        communityId,
        type,
        rangeType,
        {
          filters: {
            competitors,
            care_types: careTypes,
            room_types: roomTypes,
            room_types_price_display: roomTypesPriceDisplay,
            care_levels: careLevels,
            review_sources: reviewSources,
            review_categories: reviewCategories,
            swot_categories: swotCategories,
            contract_types: contractTypes,
          },
          ...(publication?.hash ? { public_hash: publication.hash } : {}),
        },
        { signal }
      ),
    refetchOnWindowFocus: false,
    enabled: communityId > 0 && snapshotUuids !== null,
  });

  const { data: swotCategoriesScores } = useQuery({
    queryKey: ['swot-categories-scores', communityId, competitors, publication?.hash],
    queryFn: () =>
      fetchSwotCategoriesScores(communityId, {
        competitors,
        ...(publication?.hash ? { public_hash: publication.hash } : {}),
      }),
    refetchOnWindowFocus: false,
    enabled: communityId > 0,
  });

  const scores = useMemo(() => {
    if (!swotCategoriesScores?.length || !swotAverages?.length) {
      return [];
    }

    const swotAveragesItems = swotAverages[0];

    const filteredSwotCategoriesScores = swotCategories?.length
      ? swotCategoriesScores.filter(({ category_id }: SwotCategoryScoresInbound) =>
          swotCategories.includes(category_id)
        )
      : swotCategoriesScores;

    return filteredSwotCategoriesScores.map((el: SwotCategoryScoresInbound) => {
      const categoryScore = toNullableNumber(el.display_score);
      const importanceScore = toNullableNumber(el.category_score);
      const categoryId = el.category_id;
      const swotCategory = SWOT_CATEGORIES.find((el) => el.value === categoryId);
      const subcategories =
        el.subcategories?.map((subcategory) => ({
          name: subcategory.name ?? subcategory.label ?? 'Subcategory',
          key: (subcategory as any).key,
          score: toNullableNumber(subcategory.score),
        })) ?? [];
      const averageSubcategoryScore = getAverageScore(subcategories.map((subcategory) => subcategory.score));
      const score = averageSubcategoryScore ?? toNullableNumber(swotAveragesItems[`${swotCategory?.id}_avg`]);

      return {
        categoryScore,
        score,
        categoryName: swotCategory?.label,
        subcategories,
        ['Score']: score,
        ['Category Score']: importanceScore,
      };
    });
  }, [swotCategoriesScores, swotAverages, swotCategories]);

  return (
    <Container>
      {scores?.length > 0 && <SwotPerformanceScatterGraph data={scores} />}
      {scores?.length === 0 && <NoDataInfo>No Data Available</NoDataInfo>}
    </Container>
  );
};

export default SwotPerformanceWidgetContent;
